import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toastError, toastSuccess } from "../../../../utils/pageUtil/toast";
import { adminApiService } from "../../../../utils/api";
import { messages } from "../../../../locales/messages";
import {
  CreateGfFlyerItemFigmaJobDto,
  GfFigmaTemplateData,
  MissingPositionDto,
} from "@swagger/typescript-fetch-goflyer";
import { generateFlyerItemsFlyersFn } from "../generateFlyerItemsFlyersFn";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";

export type ConfirmationModalState = {
  title: string;
  description: string;
  warningMessage?: string;
  warningSeverity?: "error" | "warning" | "info" | "success";
};

export function useFlyerGenerationHandlers(params: {
  flyerItemListId?: string;
  flyerTotal?: number;
  setJobId: (id: string | undefined) => void;
}): {
  // flow controls
  isDeleteFlow: boolean;
  setIsDeleteFlow: (v: boolean | ((prev: boolean) => boolean)) => void;
  // template selection modal
  openTemplateModal: boolean;
  setOpenTemplateModal: (v: boolean) => void;
  isGenerating: boolean;
  // confirmation modal state
  open: boolean;
  setOpen: (v: boolean) => void;
  confirmationModal: ConfirmationModalState;
  setConfirmationModal: (v: ConfirmationModalState) => void;
  // generation state
  pendingTemplate?: GfFigmaTemplateData;
  setPendingTemplate: (tpl: GfFigmaTemplateData | undefined) => void;
  generateFlyers: boolean;
  setGenerateFlyers: (v: boolean) => void;
  flyerGenerationInProgress?: number;
  setFlyerGenerationInProgress: (v: number | undefined) => void;
  // actions
  generateFlyersForFlyerItemList: (
    template?: GfFigmaTemplateData
  ) => Promise<void>;
  handleModalConfirmation: (opts: {
    generateFlyers: boolean;
    pendingTemplate?: GfFigmaTemplateData;
  }) => Promise<void>;
} {
  const { flyerItemListId, flyerTotal, setJobId } = params;
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();

  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationModalState>({
      title: "",
      description: "",
      warningMessage: "",
      warningSeverity: "warning",
    });
  const [pendingTemplate, setPendingTemplate] = useState<
    GfFigmaTemplateData | undefined
  >(undefined);
  const [generateFlyers, setGenerateFlyers] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [flyerGenerationInProgress, setFlyerGenerationInProgress] = useState<
    number | undefined
  >(undefined);

  const [isDeleteFlow, setIsDeleteFlow] = useState(false);

  const [totalItems, setTotalItems] = useState<number | undefined>(flyerTotal);

  const getFlyerItemList = useCallback(async (id: string) => {
    return adminApiService.getOneBaseGfFlyerItemListAdminControllerGfFlyerItemList(
      id,
      undefined,
      ["stores.merchant.merchantLogo"],
      undefined
    );
  }, []);

  useEffect(() => {
    const syncTotal = async () => {
      if (typeof flyerTotal === "number") {
        setTotalItems(flyerTotal);
        return;
      }
    };
    syncTotal();
  }, [flyerTotal, flyerItemListId]);

  const generateFlyersForFlyerItemList = useCallback(
    async (template?: GfFigmaTemplateData) => {
      if (!flyerItemListId) {
        toastError(
          "Missing flyer item list. Please reopen the page and try again."
        );
        setOpenTemplateModal(false);
        return;
      }
      if (!template?.id) {
        toastError("Please select a template to continue.");
        setOpenTemplateModal(false);
        return;
      }
      if (flyerTotal == null) {
        toastError(
          "Couldn't determine total items. Please refresh and try again."
        );
        setOpenTemplateModal(false);
        return;
      }
      try {
        const existingJobResponse =
          await adminApiService.getManyBaseGfFlyerItemFigmaJobAdminControllerGfFlyerItemFigmaJob(
            undefined,
            undefined,
            [
              `flyerItemList.id||eq||${flyerItemListId}`,
              `status||in||${CreateGfFlyerItemFigmaJobDto.StatusEnum.Waiting},${CreateGfFlyerItemFigmaJobDto.StatusEnum.InProgress}`,
            ],
            undefined,
            ["updatedAt,DESC"],
            [],
            1
          );
        if (existingJobResponse?.data?.[0]) {
          toastError("A flyer generation job is already running.");
          return;
        }

        const validationResponse =
          await adminApiService.gfFlyerItemFigmaJobAdminControllerValidateComponentPositions(
            flyerItemListId,
            template?.id,
            authHeaders
          );

        if (
          validationResponse?.data &&
          validationResponse.data?.templateHasPositions === false
        ) {
          setGenerateFlyers(false);
          setIsDeleteFlow(false);
          setOpen(true);
          setConfirmationModal({
            title: "Template not ready for generation",
            warningSeverity: "info",
            description: [
              "<p>The selected template doesn't include any layout positions yet, so we can't place products.</p>",
              "<p>Please try one of the following:</p>",
              "<ul style='padding-left:16px; margin-top:8px;'>",
              "<li>Pick a different template.</li>",
              "<li>Contact an administrator to generate the layout for this template.</li>",
              "<li>Try again later once positions have been added.</li>",
              "</ul>",
            ].join(""),
          });
          return;
        }

        const missing = validationResponse?.data?.missing as
          | MissingPositionDto[]
          | undefined;
        setPendingTemplate(template);
        setGenerateFlyers(true);
        setIsDeleteFlow(false);
        setOpen(true);
        const baseDesc = t(messages.flyerGenerationPrompt());
        let warningMessage = "";
        if (validationResponse?.data?.hasMissing && missing?.length) {
          warningMessage = `${missing.length} categories missing component positions.`;
        }
        const missingListHtml = missing?.length
          ? `<ul style='padding-left:16px; margin-top:8px;'>${missing
              .map(
                (m) =>
                  `<li><strong>${m.name}</strong> (items: ${m.count}${
                    m.front ? ", front page" : ""
                  })</li>`
              )
              .join("")}</ul>`
          : "";
        setConfirmationModal({
          title: `${t(messages.flyerGenerationConfirmation())}`,
          description: `${baseDesc}${missingListHtml}`,
          warningMessage,
        });
      } catch (e) {
        toastError("Failed to validate component positions");
      } finally {
        setOpenTemplateModal(false);
      }
    },
    [
      flyerItemListId,
      flyerTotal,
      authHeaders,
      t,
      openTemplateModal,
      open,
      confirmationModal,
      pendingTemplate,
      generateFlyers,
    ]
  );

  const handleModalConfirmation = useCallback(
    async (opts: {
      generateFlyers: boolean;
      pendingTemplate?: GfFigmaTemplateData;
    }) => {
      const { generateFlyers, pendingTemplate } = opts;
      if (generateFlyers && !isDeleteFlow) {
        setOpen(false);
        setIsGenerating(true);
        try {
          if (!flyerItemListId) {
            toastError(
              "Missing flyer item list. Please reopen the page and try again."
            );
            return;
          }

          if (totalItems == null) {
            toastError(
              "Couldn't determine total items. Please refresh and try again."
            );
            return;
          }
          const response = await getFlyerItemList(flyerItemListId);
          const jobObject = await generateFlyerItemsFlyersFn(
            response,
            totalItems || 0,
            pendingTemplate,
            authHeaders
          );
          if (jobObject && jobObject.id) {
            setJobId(jobObject.id);
            setFlyerGenerationInProgress(totalItems);
            toastSuccess("Flyer generation started");
          }
        } catch (e) {
          toastError("Failed to start flyer generation");
        } finally {
          setIsGenerating(false);
        }
      } else if (isDeleteFlow) {
        // Deletion is handled by caller after this returns
      } else {
        // Info-only modal: just close
        setOpen(false);
      }
    },
    [
      isDeleteFlow,
      setOpen,
      setIsGenerating,
      getFlyerItemList,
      totalItems,
      setJobId,
      setFlyerGenerationInProgress,
    ]
  );

  return {
    isDeleteFlow,
    setIsDeleteFlow,
    openTemplateModal,
    setOpenTemplateModal,
    isGenerating,
    open,
    setOpen,
    confirmationModal,
    setConfirmationModal,
    pendingTemplate,
    setPendingTemplate,
    generateFlyers,
    setGenerateFlyers,
    flyerGenerationInProgress,
    setFlyerGenerationInProgress,
    generateFlyersForFlyerItemList,
    handleModalConfirmation,
  };
}
